import { ProtocolGuards } from "../../protocol.js";
export class AuthProtocolGuards {
    validVerifyOutcome(value) {
        if (!ProtocolGuards.record(value) || !this.validOutcome(value))
            return false;
        if (value.auth === undefined)
            return true;
        if (!ProtocolGuards.record(value.auth))
            return false;
        const positions = value.auth.positions;
        return ((value.auth.isPublic === undefined ||
            typeof value.auth.isPublic === "boolean") &&
            (positions === undefined ||
                (Array.isArray(positions) &&
                    positions.every((position) => typeof position === "string"))));
    }
    validOutcome(value) {
        if (!ProtocolGuards.record(value))
            return false;
        const envelopes = [value];
        if (value.auth !== undefined) {
            if (!ProtocolGuards.record(value.auth))
                return false;
            envelopes.push(value.auth);
        }
        for (const envelope of envelopes) {
            for (const key of ["mfaRequired", "mfaEmailSent"]) {
                if (envelope[key] !== undefined && typeof envelope[key] !== "boolean")
                    return false;
            }
            for (const key of [
                "mfaExpireDate",
                "token",
                "tenantCode",
                "userTenantToken",
                "userId",
                "name",
                "email",
                "expireDate",
                "title",
                "pictureUrl",
            ]) {
                if (envelope[key] !== undefined && typeof envelope[key] !== "string")
                    return false;
            }
            for (const key of ["roles", "groups"]) {
                const entries = envelope[key];
                if (entries !== undefined &&
                    (!Array.isArray(entries) ||
                        entries.some((entry) => typeof entry !== "string")))
                    return false;
            }
        }
        return ((value.message === undefined || typeof value.message === "string") &&
            (value.status === undefined ||
                (typeof value.status === "number" && !Number.isNaN(value.status))));
    }
    jwt = {
        safeParse(value) {
            if (!ProtocolGuards.record(value) ||
                (value.exp !== undefined &&
                    (typeof value.exp !== "number" || Number.isNaN(value.exp))) ||
                (value.tenantCode !== undefined && typeof value.tenantCode !== "string"))
                return { success: false };
            return {
                success: true,
                data: {
                    ...value,
                    ...(typeof value.exp === "number" ? { exp: value.exp } : {}),
                    ...(typeof value.tenantCode === "string"
                        ? { tenantCode: value.tenantCode }
                        : {}),
                },
            };
        },
    };
    notification = {
        safeParse(value) {
            if (!ProtocolGuards.record(value) ||
                value.type !== "logout" ||
                typeof value.tenantCode !== "string" ||
                !value.tenantCode.length ||
                typeof value.allTenants !== "boolean")
                return { success: false };
            return {
                success: true,
                data: {
                    type: "logout",
                    tenantCode: value.tenantCode,
                    allTenants: value.allTenants,
                },
            };
        },
    };
    mfa(value) {
        if (!ProtocolGuards.record(value))
            return undefined;
        const direct = this.challenge(value);
        return direct ?? this.challenge(value.auth);
    }
    challenge(value) {
        if (!ProtocolGuards.record(value) ||
            value.mfaRequired !== true ||
            (value.mfaEmailSent !== undefined &&
                typeof value.mfaEmailSent !== "boolean") ||
            (value.mfaExpireDate !== undefined &&
                typeof value.mfaExpireDate !== "string"))
            return undefined;
        return {
            mfaRequired: true,
            ...(typeof value.mfaEmailSent === "boolean"
                ? { mfaEmailSent: value.mfaEmailSent }
                : {}),
            ...(typeof value.mfaExpireDate === "string"
                ? { mfaExpireDate: value.mfaExpireDate }
                : {}),
        };
    }
    response(value) {
        if (!ProtocolGuards.record(value) ||
            (value.message !== undefined && typeof value.message !== "string") ||
            (value.status !== undefined &&
                (typeof value.status !== "number" || Number.isNaN(value.status)))) {
            throw new Error("Invalid authentication response.");
        }
        return {
            ...value,
            ...(typeof value.message === "string" ? { message: value.message } : {}),
            ...(typeof value.status === "number" ? { status: value.status } : {}),
        };
    }
    reset(value) {
        const response = this.response(value);
        if (!ProtocolGuards.record(value) ||
            (value.emailSent !== undefined && typeof value.emailSent !== "boolean")) {
            throw new Error("Invalid password reset response.");
        }
        return {
            ...response,
            ...(typeof value.emailSent === "boolean"
                ? { emailSent: value.emailSent }
                : {}),
        };
    }
}
export class ComposedAuthProtocol {
    guards;
    additional;
    constructor(guards, additional) {
        this.guards = guards;
        this.additional = additional;
    }
    validVerifyOutcome(value) {
        const guarded = this.guards.validVerifyOutcome(value);
        const additional = this.additional?.validVerifyOutcome(value) ?? true;
        return guarded && additional;
    }
    validOutcome(value) {
        const guarded = this.guards.validOutcome(value);
        const additional = this.additional?.validOutcome(value) ?? true;
        return guarded && additional;
    }
    jwt = {
        safeParse: (value) => {
            const guarded = this.guards.jwt.safeParse(value);
            const additional = this.additional?.jwt.safeParse(value);
            return guarded.success ? (additional ?? guarded) : guarded;
        },
    };
    notification = {
        safeParse: (value) => {
            const guarded = this.guards.notification.safeParse(value);
            const additional = this.additional?.notification.safeParse(value);
            return guarded.success ? (additional ?? guarded) : guarded;
        },
    };
    mfa(value) {
        const guarded = this.guards.mfa(value);
        return guarded && (this.additional ? this.additional.mfa(value) : guarded);
    }
    response(value) {
        let guarded;
        try {
            guarded = this.guards.response(value);
        }
        catch (error) {
            this.additional?.response(value);
            throw error;
        }
        return this.additional?.response(value) ?? guarded;
    }
    reset(value) {
        let guarded;
        try {
            guarded = this.guards.reset(value);
        }
        catch (error) {
            this.additional?.reset(value);
            throw error;
        }
        return this.additional?.reset(value) ?? guarded;
    }
}
//# sourceMappingURL=auth-protocol.js.map